// Full-screen result shown by the Authorization gate when the signed-in user is
// NOT allowed into the Control Center ("forbidden") or when access couldn't be
// verified ("error"). Both offer Sign out; the error variant also offers Retry.
// Never renders any app data — it replaces the shell entirely.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, Result } from "src/assets/imports/antdComponents";
import { RootState } from "src/store";
import { getCognitoIdentity } from "src/services/cognitoAuth";
import { signOutFromApp } from "src/services/authorization";

interface AccessResultProps {
	variant: "forbidden" | "error";
	// For the "error" variant: was it a server/gateway error or an unreachable
	// network? Defaults to server (the common 5xx/502 case).
	reason?: "server" | "network";
	onRetry?: () => void;
}

const AccessResult: React.FC<AccessResultProps> = ({ onRetry, reason, variant }) => {
	const { t } = useTranslation();
	const emailFromStore = useSelector((state: RootState) => state.authSlice.email);
	const [email, setEmail] = useState<string | null>(emailFromStore);
	const [signingOut, setSigningOut] = useState(false);

	// A mid-session 403 flips the store to forbidden without an email; backfill it
	// from the Cognito id-token claims for the "Signed in as …" line.
	useEffect(() => {
		if (email) return;
		let alive = true;
		getCognitoIdentity()
			.then((identity) => {
				if (alive && identity?.email) setEmail(identity.email);
			})
			.catch(() => undefined);
		return () => {
			alive = false;
		};
	}, [email]);

	const handleSignOut = async () => {
		setSigningOut(true);
		try {
			await signOutFromApp();
		} catch {
			// Sign-out redirect failed — re-enable the button so the user can retry.
			setSigningOut(false);
		}
	};

	const forbidden = variant === "forbidden";
	const offline = reason === "network";

	let status: "403" | "500" | "warning";
	let title: string;
	let subTitle: string;
	if (forbidden) {
		// Genuine authorization denial — the user is signed in but not provisioned.
		status = "403";
		title = t("You don't have access to Kural Control Center");
		subTitle = t(
			"Your account isn't provisioned for the Control Center. Please contact an administrator to request access.",
		);
	} else if (offline) {
		// We never reached the server (offline / CORS / timeout).
		status = "warning";
		title = t("Can't reach the server");
		subTitle = t("Check your internet connection and try again.");
	} else {
		// The server/gateway replied with an error (5xx / 502) — a service outage,
		// NOT an access problem. Frame it as temporary and retryable.
		status = "500";
		title = t("Kural Control Center is temporarily unavailable");
		subTitle = t("The server isn't responding right now. This is usually temporary - please try again in a moment.");
	}

	return (
		<div className="flex flex-col justify-center items-center h-screen w-screen bg-[#F2F6FA]">
			<Result
				status={status}
				title={title}
				subTitle={
					<>
						<div>{subTitle}</div>
						{email && (
							<div className="mt-2 text-[--neutral-grey]">{t("Signed in as {{email}}", { email })}</div>
						)}
					</>
				}
				extra={
					<div className="flex items-center justify-center gap-3">
						{onRetry && (
							<Button type="primary" className="kcc-modal__primary-btn" onClick={onRetry}>
								{/* Forbidden = re-verify with the server (access may have been
								    restored, or the 403 was incidental); error = plain retry. */}
								{forbidden ? t("Re-check access") : t("Try again")}
							</Button>
						)}
						<Button className="kcc-modal__ghost-btn" loading={signingOut} onClick={handleSignOut}>
							{t("Sign out")}
						</Button>
					</div>
				}
			/>
		</div>
	);
};

export default AccessResult;
